import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { getDateLocale } from '../i18n';
import { createBadgeAwardDispute } from '../services/api';

const BadgeAwardDisputeModal = ({ isOpen, onClose, badgeAward, onSuccess }) => {
  const { t } = useTranslation(['profile', 'common']);
  const [reason, setReason] = useState('');
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!reason.trim()) {
      setError(t('reasonRequired'));
      return;
    }

    setError(null);
    setLoading(true);

    try {
      await createBadgeAwardDispute(badgeAward.id, reason.trim());
      setSuccess(true);
      setTimeout(() => {
        onSuccess?.();
        handleClose();
      }, 1500);
    } catch (err) {
      console.error('Failed to create dispute:', err);
      setError(err.data?.badge_award_id?.[0] || err.data?.reason?.[0] || err.data?.detail || t('submitDisputeFailed'));
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) {
      setReason('');
      setError(null);
      setSuccess(false);
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black bg-opacity-50">
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.95 }}
          className="bg-white dark:bg-slate-800 rounded-xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto border border-slate-200 dark:border-slate-600"
        >
          {/* Header */}
          <div className="sticky top-0 bg-white dark:bg-slate-800 border-b border-slate-200 dark:border-slate-600 px-6 py-4 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-red-50 dark:bg-red-900/30 rounded-lg">
                <span className="material-symbols-outlined text-red-600 dark:text-red-400 text-2xl">report_problem</span>
              </div>
              <div>
                <h2 className="text-xl font-bold text-slate-900 dark:text-slate-100">{t('reportAnomaly')}</h2>
                <p className="text-sm text-slate-600 dark:text-slate-400">{t('fileDisputeSubtitle')}</p>
              </div>
            </div>
            <button
              onClick={handleClose}
              disabled={loading}
              className="p-2 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-lg transition-colors disabled:opacity-50"
            >
              <span className="material-symbols-outlined text-slate-600 dark:text-slate-400">close</span>
            </button>
          </div>

          {/* Content */}
          <div className="p-6">
            {success ? (
              <div className="text-center py-8">
                <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <span className="material-symbols-outlined text-green-600 text-3xl">check_circle</span>
                </div>
                <h3 className="text-xl font-semibold text-slate-900 mb-2">{t('disputeSubmittedTitle')}</h3>
                <p className="text-slate-600">{t('disputeSubmittedBody')}</p>
              </div>
            ) : (
              <>
                {/* Badge Award Info */}
                {badgeAward && (
                  <div className="bg-slate-50 dark:bg-slate-700/50 rounded-lg p-4 mb-6 border border-slate-200 dark:border-slate-600">
                    <h3 className="font-semibold text-slate-900 dark:text-slate-100 mb-2">{t('badgeAwardDetails')}</h3>
                    <div className="space-y-1 text-sm">
                      <div className="flex items-center gap-2">
                        <span className="material-symbols-outlined text-slate-500 dark:text-slate-400 text-lg">emoji_events</span>
                        <span className="text-slate-700 dark:text-slate-300"><strong>{t('badgeLabel')}</strong> {badgeAward.badge?.name || t('common:na')}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="material-symbols-outlined text-slate-500 dark:text-slate-400 text-lg">calendar_today</span>
                        <span className="text-slate-700 dark:text-slate-300">
                          <strong>{t('awardedLabel')}</strong>{' '}
                          {new Date(badgeAward.awarded_date).toLocaleDateString(getDateLocale(), {
                            year: 'numeric',
                            month: 'long',
                            day: 'numeric'
                          })}
                        </span>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="material-symbols-outlined text-slate-500 dark:text-slate-400 text-lg">stars</span>
                        <span className="text-slate-700 dark:text-slate-300"><strong>{t('pointsLabel')}</strong> {badgeAward.points_earned || 0}</span>
                      </div>
                    </div>
                  </div>
                )}

                {/* Form */}
                <form onSubmit={handleSubmit}>
                  <div className="mb-6">
                    <label htmlFor="reason" className="block text-sm font-medium text-slate-700 mb-2">
                      {t('reasonForDispute')} <span className="text-red-500">*</span>
                    </label>
                    <textarea
                      id="reason"
                      value={reason}
                      onChange={(e) => {
                        setReason(e.target.value);
                        setError(null);
                      }}
                      placeholder={t('reasonPlaceholder')}
                      rows={6}
                      className="w-full px-4 py-3 border border-slate-300 dark:border-slate-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-none bg-white dark:bg-slate-700 text-slate-900 dark:text-slate-100 placeholder-slate-500 dark:placeholder-slate-400"
                      required
                      disabled={loading}
                    />
                    <p className="mt-2 text-xs text-slate-500">
                      {t('reasonReviewHint')}
                    </p>
                  </div>

                  {error && (
                    <div className="mb-4 p-3 bg-red-50 border border-red-200 dark:bg-red-900/30 dark:border-red-700/50 dark:text-red-200 rounded-lg">
                      <p className="text-sm text-red-700">{error}</p>
                    </div>
                  )}

                  {/* Actions */}
                  <div className="flex items-center justify-end gap-3">
                    <button
                      type="button"
                      onClick={handleClose}
                      disabled={loading}
                      className="px-4 py-2 text-slate-700 bg-slate-100 rounded-lg hover:bg-slate-200 transition-colors disabled:opacity-50"
                    >
                      {t('common:cancel')}
                    </button>
                    <button
                      type="submit"
                      disabled={loading || !reason.trim()}
                      className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                    >
                      <span className="material-symbols-outlined text-lg">send</span>
                      <span>{t('submitDispute')}</span>
                    </button>
                  </div>
                </form>
              </>
            )}
          </div>
        </motion.div>
      </div>
    </AnimatePresence>
  );
};

export default BadgeAwardDisputeModal;
